import { Injectable, inject, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from 'src/app/theme/shared/service/authentication.service';

export interface SocketNewChat       { chat: any; }
export interface SocketNewMessage    { chatId: string; message: any; chat?: any; }
export interface SocketMessageEdited { chatId: string; message: any; }
export interface SocketRead          { chatId: string; by: string; }
export interface SocketStatus        { chatId: string; status: string; }
export interface SocketTyping     { chatId: string; senderId: string; senderRole: string; isTyping: boolean; }

export interface SocketTripDriverLocation {
  tripId:          string;
  driverId:        string;
  currentLocation: { lat: number; lng: number; angle: number };
}

export interface SocketTripStatusUpdate {
  tripId:     string;
  orderId:    string;
  status:     string;
  driverId:   string;
  customerId: string;
}

export interface SocketBevDriverUpdate {
  driverId:        string;
  isOnline:        boolean;
  rideStatus:      'available' | 'on_trip' | null;
  currentTripId:   string | null;
  currentLocation: { lat: number; lng: number; angle: number } | null;
}

export interface SocketTripChatMessage {
  tripId:  string;
  message: any;
}

export interface SocketStagingLotDriverExited {
  driverId:    string;
  geofenceId:  string;
  portName:    string;
}

export interface SocketStagingLotDriverEntered {
  driverId:    string;
  geofenceId:  string;
  portName:    string;
  enteredAt:   string;
  vehicles:    { name: string; position: number }[];
}

@Injectable({ providedIn: 'root' })
export class SupportSocketService implements OnDestroy {
  private authService = inject(AuthenticationService);

  private socket: Socket | null = null;

  readonly notificationNew$ = new Subject<any>();
  readonly chatNew$         = new Subject<SocketNewChat>();
  readonly chatUnarchived$  = new Subject<SocketNewChat>();
  readonly messageNew$   = new Subject<SocketNewMessage>();
  readonly messageEdited$ = new Subject<SocketMessageEdited>();
  readonly read$         = new Subject<SocketRead>();
  readonly status$       = new Subject<SocketStatus>();
  readonly typing$       = new Subject<SocketTyping>();
  readonly driverUpdate$       = new Subject<SocketBevDriverUpdate>();
  readonly tripStatusUpdate$   = new Subject<SocketTripStatusUpdate>();
  readonly tripDriverLocation$ = new Subject<SocketTripDriverLocation>();
  readonly tripChatMessage$    = new Subject<SocketTripChatMessage>();
  readonly stagingLotDriverExited$  = new Subject<SocketStagingLotDriverExited>();
  readonly stagingLotDriverEntered$ = new Subject<SocketStagingLotDriverEntered>();

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  connect(): void {
    if (this.socket?.connected) return;

    const token = this.authService.getToken();

    this.socket = io(environment.socketUrl, {
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    this.socket.on('connect',    () => console.log('[SupportSocket] connected', this.socket?.id));
    this.socket.on('disconnect', (reason) => console.log('[SupportSocket] disconnected', reason));
    this.socket.on('connect_error', (err) => console.warn('[SupportSocket] error', err.message));

    // Admin notification room — server auto-joins on connect (user:<adminId>)
    this.socket.on('notification:new', (data: any) => this.notificationNew$.next(data));

    // Global room (support:admins) — auto-joined on connect
    this.socket.on('support:chat:new', (data: SocketNewChat)       => this.chatNew$.next(data));
    this.socket.on('support:chat:unarchived', (data: SocketNewChat) => this.chatUnarchived$.next(data));

    // Per-chat room events — active after emitting support:admin:join
    this.socket.on('support:message:new',    (data: SocketNewMessage)    => this.messageNew$.next(data));
    this.socket.on('support:message:edited', (data: SocketMessageEdited) => this.messageEdited$.next(data));
    this.socket.on('support:read',           (data: SocketRead)          => this.read$.next(data));
    this.socket.on('support:status',      (data: SocketStatus)     => this.status$.next(data));
    this.socket.on('support:typing',      (data: SocketTyping)          => this.typing$.next(data));
    this.socket.on('birdeyeview:driver_update', (data: SocketBevDriverUpdate)     => this.driverUpdate$.next(data));
    this.socket.on('admin:trip_status_update',  (data: SocketTripStatusUpdate)    => this.tripStatusUpdate$.next(data));
    this.socket.on('trip:driver_location',      (data: SocketTripDriverLocation)  => this.tripDriverLocation$.next(data));
    this.socket.on('trip:chat_message',         (data: SocketTripChatMessage)     => this.tripChatMessage$.next(data));
    this.socket.on('stagingLot:driverExited',   (data: SocketStagingLotDriverExited)  => this.stagingLotDriverExited$.next(data));
    this.socket.on('stagingLot:driverEntered', (data: SocketStagingLotDriverEntered) => this.stagingLotDriverEntered$.next(data));
  }

  joinChat(chatId: string): void  { this.socket?.emit('support:admin:join',  chatId); }
  leaveChat(chatId: string): void { this.socket?.emit('support:admin:leave', chatId); }

  joinTrip(tripId: string): void  { this.socket?.emit('join:trip',  tripId); }
  leaveTrip(tripId: string): void { this.socket?.emit('leave:trip', tripId); }

  emitTyping(chatId: string, isTyping: boolean): void {
    this.socket?.emit('support:typing', { chatId, isTyping });
  }

  emitSend(
    payload: { chatId: string; type: 'text' | 'image' | 'file'; text?: string; attachment?: any },
    ack: (res: { success: boolean; message?: string }) => void
  ): void {
    this.socket?.emit('support:send', payload, ack);
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
